import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Activity, AlertTriangle, Battery, Bell, BellRing, CheckCircle2, ChevronRight,
  Clock, Cpu, Filter, Gauge, History, Mail, MapPin, Pencil, Plus, Radio,
  RefreshCw, Route as RouteIcon, Search, ShieldAlert, SlidersHorizontal,
  Tag, Target, Thermometer, Trash2, Truck, Webhook, X
} from 'lucide-react'
import { apiClient } from '../config/api'
import AlertRuleEditor, { EditorRule } from './AlertRuleEditor'
import { cachedPlaceName, resolvePlaceName } from '../utils/placeNames'

// Alerts whose whole meaning is "where": a zone entry at "12.59, -8.03" says
// nothing, the same entry at "Baco Djikoroni" does.
const LOCATION_TYPES = new Set(['GEOFENCE_ENTRY', 'GEOFENCE_EXIT', 'CORRIDOR_DEVIATION', 'DWELL_TIME'])

/** Place name resolved on demand, coordinates kept underneath (or as tooltip). */
const AlertPlace: React.FC<{ latitude: number; longitude: number; compact?: boolean }> = ({ latitude, longitude, compact }) => {
  const coordinates = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
  const [place, setPlace] = useState<string | null>(() => cachedPlaceName(latitude, longitude))
  useEffect(() => {
    let cancelled = false
    resolvePlaceName(latitude, longitude).then(name => {
      if (!cancelled) setPlace(name)
    })
    return () => { cancelled = true }
  }, [latitude, longitude])
  if (compact) {
    return <span title={coordinates}><MapPin size={9} className="inline mr-0.5" />{place || coordinates}</span>
  }
  return (
    <div>
      <div>{place || coordinates}</div>
      {place && <div className="text-[10px] text-gray-400">{coordinates}</div>}
    </div>
  )
}

interface FleetAlert {
  id: number
  rule_id: number | null
  customer_id: number | null
  customer_name?: string | null
  vehicle_source: string
  vehicle_id: number
  vehicle_code?: string | null
  alert_type: string
  severity?: 'INFO' | 'WARNING' | 'CRITICAL'
  signal_key?: string | null
  observed_value?: number | null
  observed_text?: string | null
  threshold_value?: number | null
  source_event_id?: number | null
  source_brand?: string | null
  context_json?: Record<string, any>
  geofence_id?: string | null
  geofence_name?: string | null
  corridor_id?: string | null
  corridor_name?: string | null
  mission_id?: string | null
  mission_name?: string | null
  message: string
  latitude?: number | null
  longitude?: number | null
  status: 'ACTIVE' | 'ACKNOWLEDGED' | 'RECOVERED' | 'RESOLVED'
  acknowledged_by?: string | null
  acknowledged_at?: string | null
  recovered_at?: string | null
  resolved_by?: string | null
  resolved_at?: string | null
  can_acknowledge?: boolean
  can_resolve?: boolean
  can_receive?: boolean
  created_at: string
  rule_name?: string | null
  rule_description?: string | null
  rule_conditions?: Record<string, any>
  rule_channels?: string[]
  fleet_name?: string | null
}

interface AlertRule extends EditorRule {
  id: number
  is_read_only?: boolean
  customer_name?: string | null
  geofence_name?: string | null
  corridor_name?: string | null
  fleet_name?: string | null
}

interface Delivery {
  id: number
  channel: string
  destination?: string | null
  delivery_status: string
  error_code?: string | null
  attempted_at: string
  delivered_at?: string | null
}

export interface AlertCreationRequest {
  requestId: number
  editingRuleId?: number
  target: {
    vehicle_source: 'ASSET' | 'CONTAINER'
    vehicle_id: number
    vehicle_code: string
    customer_id?: number | null
  }
}

interface FleetAlertsPanelProps {
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm?: (message: string) => Promise<boolean>
  creationRequest?: AlertCreationRequest | null
  /** Open this alert's detail. `token` changes on every request so the same id can be reopened. */
  focusAlert?: { id: number, token: number } | null
}

const CATEGORY_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  LOCATION: { label: 'Location', icon: MapPin, color: 'text-blue-700 bg-blue-50 border-blue-200' },
  DRIVING: { label: 'Driving', icon: Gauge, color: 'text-orange-700 bg-orange-50 border-orange-200' },
  DEVICE_HEALTH: { label: 'Device health', icon: Battery, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  SECURITY: { label: 'Security', icon: ShieldAlert, color: 'text-red-700 bg-red-50 border-red-200' },
  ENVIRONMENT: { label: 'Environment', icon: Thermometer, color: 'text-cyan-700 bg-cyan-50 border-cyan-200' },
  OBD: { label: 'OBD / vehicle', icon: Cpu, color: 'text-purple-700 bg-purple-50 border-purple-200' },
  TAGS: { label: 'Tags', icon: Tag, color: 'text-pink-700 bg-pink-50 border-pink-200' },
  LOGISTICS: { label: 'Logistics', icon: Truck, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' }
}

const TYPE_CATEGORY: Record<string, string> = {
  GEOFENCE_ENTRY: 'LOCATION',
  GEOFENCE_EXIT: 'LOCATION',
  CORRIDOR_DEVIATION: 'LOCATION',
  SPEED_THRESHOLD: 'DRIVING',
  BATTERY_LOW: 'DEVICE_HEALTH',
  SIGNAL_LOW: 'DEVICE_HEALTH',
  DEVICE_OFFLINE: 'DEVICE_HEALTH',
  GPS_SIGNAL_LOST: 'DEVICE_HEALTH',
  LOW_BATTERY_EVENT: 'DEVICE_HEALTH',
  POWER_LOSS: 'DEVICE_HEALTH',
  DEVICE_ALERT: 'DEVICE_HEALTH',
  LOCK_STATE: 'SECURITY',
  TAMPER: 'SECURITY',
  VIBRATION: 'SECURITY',
  SOS_PANIC: 'SECURITY',
  TEMPERATURE_THRESHOLD: 'ENVIRONMENT',
  HUMIDITY_THRESHOLD: 'ENVIRONMENT',
  IGNITION_STATE: 'OBD',
  RPM_THRESHOLD: 'OBD',
  COOLANT_TEMPERATURE: 'OBD',
  FUEL_LEVEL: 'OBD',
  ODOMETER_THRESHOLD: 'OBD',
  DIAGNOSTIC_EVENT: 'OBD',
  TAG_BATTERY_LOW: 'TAGS',
  TAG_TEMPERATURE: 'TAGS',
  TAG_MISSING: 'TAGS',
  DWELL_TIME: 'LOGISTICS',
  STOPPED_TOO_LONG: 'LOGISTICS'
}

const TYPE_LABELS: Record<string, string> = {
  GEOFENCE_ENTRY: 'Zone entry',
  GEOFENCE_EXIT: 'Zone exit',
  CORRIDOR_DEVIATION: 'Trajectory deviation',
  SPEED_THRESHOLD: 'Speed threshold',
  BATTERY_LOW: 'Low battery',
  SIGNAL_LOW: 'Weak signal',
  DEVICE_OFFLINE: 'Device offline',
  GPS_SIGNAL_LOST: 'GPS signal lost',
  LOW_BATTERY_EVENT: 'Low-battery device alarm',
  POWER_LOSS: 'Power loss',
  DEVICE_ALERT: 'Device alarm',
  LOCK_STATE: 'Lock / seal state',
  TAMPER: 'Tamper',
  VIBRATION: 'Vibration',
  SOS_PANIC: 'SOS / panic',
  TEMPERATURE_THRESHOLD: 'Temperature',
  HUMIDITY_THRESHOLD: 'Humidity',
  IGNITION_STATE: 'Ignition',
  RPM_THRESHOLD: 'Engine RPM',
  COOLANT_TEMPERATURE: 'Coolant temperature',
  FUEL_LEVEL: 'Fuel level',
  ODOMETER_THRESHOLD: 'Odometer',
  DIAGNOSTIC_EVENT: 'Diagnostic fault',
  TAG_BATTERY_LOW: 'Tag battery',
  TAG_TEMPERATURE: 'Tag temperature',
  TAG_MISSING: 'Tag missing',
  DWELL_TIME: 'Zone dwell',
  STOPPED_TOO_LONG: 'Stopped too long'
}

const STATUS_STYLE: Record<string, string> = {
  ACTIVE: 'bg-red-600 text-white',
  ACKNOWLEDGED: 'bg-amber-500 text-white',
  RECOVERED: 'bg-emerald-600 text-white',
  RESOLVED: 'bg-slate-500 text-white'
}

const SEVERITY_STYLE: Record<string, string> = {
  INFO: 'border-sky-200 bg-sky-50 text-sky-700',
  WARNING: 'border-amber-200 bg-amber-50 text-amber-700',
  CRITICAL: 'border-red-300 bg-red-50 text-red-800'
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const fmtDate = (value?: string | null) => value ? new Date(value).toLocaleString() : '—'

const FleetAlertsPanel: React.FC<FleetAlertsPanelProps> = ({ showMessage, showConfirm, creationRequest, focusAlert }) => {
  const [tab, setTab] = useState<'active' | 'history' | 'rules'>('active')
  const [alerts, setAlerts] = useState<FleetAlert[]>([])
  const [rules, setRules] = useState<AlertRule[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [fleets, setFleets] = useState<any[]>([])
  const [vehicles, setVehicles] = useState<any[]>([])
  const [sourceBrands, setSourceBrands] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedAlert, setSelectedAlert] = useState<FleetAlert | null>(null)
  const [presetVehicleTarget, setPresetVehicleTarget] = useState<AlertCreationRequest['target'] | null>(null)

  useEffect(() => {
    if (!creationRequest) return
    setTab('rules')
    setPresetVehicleTarget(creationRequest.target)
    if (creationRequest.editingRuleId) {
      apiClient.get('/alert-rules')
        .then(response => {
          const rows = Array.isArray(response.data) ? response.data : []
          const match = rows.find((rule: AlertRule) => Number(rule.id) === Number(creationRequest.editingRuleId))
          if (match) {
            setEditingRule(match)
            setEditorOpen(true)
          } else {
            showMessage?.('Alert rule could not be found', 'error')
          }
        })
        .catch(() => showMessage?.('Failed to load alert rule', 'error'))
    } else {
      setEditingRule(null)
      setEditorOpen(true)
    }
  }, [creationRequest?.requestId])
  const [deliveries, setDeliveries] = useState<Delivery[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filters, setFilters] = useState({
    search: '',
    severity: '',
    category: '',
    customerId: '',
    fleetId: '',
    vehicleKey: '',
    sourceBrand: ''
  })

  const loadReferences = useCallback(async () => {
    const getArray = (path: string) => apiClient.get(path)
      .then(response => Array.isArray(response.data) ? response.data : [])
      .catch(() => [])
    const [customerRows, fleetRows, vehicleRows, telemetryRows] = await Promise.all([
      getArray('/customers'),
      getArray('/fleets'),
      getArray('/vehicles'),
      getArray('/telemetry/availability')
    ])
    setCustomers(customerRows)
    setFleets(fleetRows)
    setVehicles(vehicleRows)
    setSourceBrands([...new Set(telemetryRows.map(row => row.source_brand).filter(Boolean).map(String))].sort())
  }, [])

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true)
      const status = tab === 'history' ? 'RECOVERED,RESOLVED' : 'ACTIVE,ACKNOWLEDGED'
      const params = new URLSearchParams({ status })
      if (filters.search.trim()) params.set('search', filters.search.trim())
      if (filters.severity) params.set('severity', filters.severity)
      if (filters.category) params.set('category', filters.category)
      if (filters.customerId) params.set('customer_id', filters.customerId)
      if (filters.fleetId) params.set('fleet_id', filters.fleetId)
      if (filters.vehicleKey) {
        const [source, id] = filters.vehicleKey.split(':')
        params.set('vehicle_source', source)
        params.set('vehicle_id', id)
      }
      if (filters.sourceBrand) params.set('source_brand', filters.sourceBrand)
      const response = await apiClient.get(`/alerts?${params.toString()}`)
      setAlerts(Array.isArray(response.data?.alerts) ? response.data.alerts : [])
    } catch (error) {
      console.error('Failed to load alerts:', error)
      setAlerts([])
    } finally {
      setLoading(false)
    }
  }, [tab, filters])

  const loadRules = useCallback(async () => {
    try {
      const response = await apiClient.get('/alert-rules')
      setRules(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('Failed to load alert rules:', error)
      setRules([])
    }
  }, [])

  useEffect(() => {
    loadReferences()
  }, [loadReferences])

  useEffect(() => {
    if (tab === 'rules') loadRules()
    else loadAlerts()
  }, [tab, loadAlerts, loadRules])

  useEffect(() => {
    if (tab === 'rules') return
    const timer = setInterval(loadAlerts, 30000)
    return () => clearInterval(timer)
  }, [tab, loadAlerts])

  const transition = async (alert: FleetAlert, action: 'acknowledge' | 'resolve') => {
    try {
      setBusyId(alert.id)
      await apiClient.post(`/alerts/${alert.id}/${action}`, {})
      showMessage?.(action === 'acknowledge' ? 'Alert acknowledged' : 'Alert resolved', 'success')
      await loadAlerts()
      if (detailOpen) setDetailOpen(false)
    } catch (error) {
      console.error(`Failed to ${action} alert:`, error)
      showMessage?.(`Failed to ${action} alert`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  useEffect(() => {
    if (!focusAlert) return
    setTab('active')
    setSelectedAlert({ id: focusAlert.id } as FleetAlert)
    setDeliveries([])
    setDetailOpen(true)
    setDetailLoading(true)
    apiClient.get(`/alerts/${focusAlert.id}`)
      .then(response => {
        if (response.data?.alert) setSelectedAlert(response.data.alert)
        setDeliveries(Array.isArray(response.data?.deliveries) ? response.data.deliveries : [])
      })
      .catch(error => console.error('Failed to load alert details:', error))
      .finally(() => setDetailLoading(false))
  }, [focusAlert?.token])

  const openDetail = async (alert: FleetAlert) => {
    setSelectedAlert(alert)
    setDeliveries([])
    setDetailOpen(true)
    setDetailLoading(true)
    try {
      const response = await apiClient.get(`/alerts/${alert.id}`)
      setSelectedAlert(response.data?.alert || alert)
      setDeliveries(Array.isArray(response.data?.deliveries) ? response.data.deliveries : [])
    } catch (error) {
      console.error('Failed to load alert details:', error)
    } finally {
      setDetailLoading(false)
    }
  }

  const deleteRule = async (rule: AlertRule) => {
    if (showConfirm && !await showConfirm(`Delete alert rule "${rule.name}"?`)) return
    try {
      await apiClient.delete(`/alert-rules/${rule.id}`)
      showMessage?.('Alert rule deleted', 'success')
      loadRules()
    } catch (error) {
      console.error('Failed to delete rule:', error)
      showMessage?.('Failed to delete alert rule', 'error')
    }
  }

  const activeFilterCount = Object.values(filters).filter(Boolean).length
  const categoryFor = (type: string) => TYPE_CATEGORY[type] || 'DEVICE_HEALTH'
  const categoryMeta = (type: string) => CATEGORY_META[categoryFor(type)] || CATEGORY_META.DEVICE_HEALTH

  const renderAlertCard = (alert: FleetAlert) => {
    const category = categoryMeta(alert.alert_type)
    const Icon = category.icon
    const observed = alert.observed_value ?? alert.observed_text
    return (
      <button
        key={alert.id}
        onClick={() => openDetail(alert)}
        className={`w-full text-left p-4 rounded-xl border transition-all hover:shadow-sm ${
          alert.severity === 'CRITICAL' && alert.status === 'ACTIVE'
            ? 'bg-red-50 border-red-300'
            : alert.status === 'ACKNOWLEDGED'
              ? 'bg-amber-50 border-amber-200'
              : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg border ${category.color}`}>
            <Icon size={17} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-semibold text-gray-900">
                {TYPE_LABELS[alert.alert_type] || alert.alert_type}
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[alert.status]}`}>
                {alert.status}
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${SEVERITY_STYLE[alert.severity || 'WARNING']}`}>
                {alert.severity || 'WARNING'}
              </span>
              {alert.source_brand && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  {alert.source_brand}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-700 mt-1">{alert.message}</p>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-gray-500">
              <span className="font-medium">{alert.vehicle_code || `${alert.vehicle_source} ${alert.vehicle_id}`}</span>
              <span>{fmtDate(alert.created_at)}</span>
              {observed != null && (
                <span className="font-medium text-gray-700">
                  Observed: {String(observed)}
                  {alert.threshold_value != null && ` / threshold ${alert.threshold_value}`}
                </span>
              )}
              {alert.geofence_name && <span><MapPin size={9} className="inline mr-0.5" />{alert.geofence_name}</span>}
              {alert.corridor_name && <span><RouteIcon size={9} className="inline mr-0.5" />{alert.corridor_name}</span>}
              {alert.mission_name && <span><Target size={9} className="inline mr-0.5" />{alert.mission_name}</span>}
              {LOCATION_TYPES.has(alert.alert_type) && alert.latitude != null && alert.longitude != null && (
                <AlertPlace latitude={Number(alert.latitude)} longitude={Number(alert.longitude)} compact />
              )}
            </div>
          </div>
          <ChevronRight size={16} className="text-gray-300 mt-2" />
        </div>
      </button>
    )
  }

  const ruleScope = (rule: AlertRule) => {
    if (rule.mission_id) return `Mission ${rule.mission_id}`
    if (rule.fleet_id != null) return `Fleet: ${rule.fleet_name || rule.fleet_id}`
    if (rule.vehicle_targets?.length) return `${rule.vehicle_targets.length} selected vehicle(s)`
    if (rule.vehicle_id != null) return `Vehicle: ${rule.vehicle_source} ${rule.vehicle_id}`
    if (rule.target_device_type) return `${rule.target_device_type} devices`
    return 'All vehicles'
  }

  const ruleCondition = (rule: AlertRule) => {
    const condition = rule.conditions || {}
    if (condition.threshold == null) return null
    const op: Record<string, string> = { gt: '>', gte: '≥', lt: '<', lte: '≤', eq: '=', neq: '≠' }
    return `${rule.signal_key || 'value'} ${op[condition.operator] || condition.operator || '='} ${condition.threshold}`
  }

  const clearFilters = () => setFilters({
    search: '', severity: '', category: '', customerId: '', fleetId: '',
    vehicleKey: '', sourceBrand: ''
  })

  return (
    <div className="h-full min-h-0 flex flex-col bg-white relative">
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <BellRing size={20} className="text-red-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Fleet Alerts</h2>
              <p className="text-[10px] text-gray-500">Location, telemetry, sensors and device events</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {tab !== 'rules' && (
              <button
                onClick={() => setFiltersOpen(value => !value)}
                className={`relative p-2 rounded-lg ${filtersOpen ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-100'}`}
                title="Filters"
              >
                <Filter size={16} />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => tab === 'rules' ? loadRules() : loadAlerts()}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          {([
            ['active', 'Active', Bell],
            ['history', 'History', History],
            ['rules', 'Rules', SlidersHorizontal]
          ] as const).map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${
                tab === id ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={14} />
              {label}
              {id === 'active' && tab !== 'rules' && alerts.filter(alert => alert.status === 'ACTIVE').length > 0 && (
                <span className="px-1.5 py-0.5 text-[9px] bg-red-600 text-white rounded-full">
                  {alerts.filter(alert => alert.status === 'ACTIVE').length}
                </span>
              )}
            </button>
          ))}
        </div>

        {filtersOpen && tab !== 'rules' && (
          <div className="mt-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-2.5 text-gray-400" />
                <input
                  value={filters.search}
                  onChange={event => setFilters(current => ({ ...current, search: event.target.value }))}
                  placeholder="Vehicle or message"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-xs bg-white"
                />
              </div>
              <select value={filters.severity} onChange={event => setFilters(current => ({ ...current, severity: event.target.value }))}
                className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs bg-white">
                <option value="">All severities</option>
                <option value="CRITICAL">Critical</option>
                <option value="WARNING">Warning</option>
                <option value="INFO">Information</option>
              </select>
              <select value={filters.category} onChange={event => setFilters(current => ({ ...current, category: event.target.value }))}
                className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs bg-white">
                <option value="">All categories</option>
                {Object.entries(CATEGORY_META).map(([id, meta]) => <option key={id} value={id}>{meta.label}</option>)}
              </select>
              <select value={filters.customerId} onChange={event => setFilters(current => ({ ...current, customerId: event.target.value, fleetId: '' }))}
                className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs bg-white">
                <option value="">All customers</option>
                {customers.map(customer => <option key={customer.id} value={customer.id}>{customer.company_name}</option>)}
              </select>
              <select value={filters.fleetId} onChange={event => setFilters(current => ({ ...current, fleetId: event.target.value }))}
                className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs bg-white">
                <option value="">All fleets</option>
                {fleets.filter(fleet => !filters.customerId || String(fleet.customer_id) === filters.customerId)
                  .map(fleet => <option key={fleet.id} value={fleet.id}>{fleet.name}</option>)}
              </select>
              <select value={filters.vehicleKey} onChange={event => setFilters(current => ({ ...current, vehicleKey: event.target.value }))}
                className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs bg-white">
                <option value="">All vehicles</option>
                {vehicles.map(vehicle => {
                  const key = `${vehicle.vehicle_source}:${vehicle.vehicle_id}`
                  return <option key={key} value={key}>{vehicle.custom_name || vehicle.vehicle_code || key}</option>
                })}
              </select>
              <select value={filters.sourceBrand} onChange={event => setFilters(current => ({ ...current, sourceBrand: event.target.value }))}
                className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs bg-white">
                <option value="">All source devices</option>
                {sourceBrands.map(brand => <option key={brand} value={brand}>{brand}</option>)}
              </select>
            </div>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="mt-2 text-[11px] text-blue-600 hover:underline">Clear all filters</button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {tab !== 'rules' && (
          alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-52 text-gray-400">
              <CheckCircle2 size={40} className="mb-2 text-green-500" />
              <div className="text-sm font-medium text-gray-600">
                {activeFilterCount > 0 ? 'No alerts match these filters' : tab === 'active' ? 'No active alerts' : 'No alert history yet'}
              </div>
              <div className="text-xs mt-1">Location and telemetry violations will appear here with their evidence.</div>
            </div>
          ) : (
            <div className="space-y-2">{alerts.map(renderAlertCard)}</div>
          )
        )}

        {tab === 'rules' && (
          <div className="space-y-3">
            <button
              onClick={() => { setEditingRule(null); setPresetVehicleTarget(null); setEditorOpen(true) }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600"
            >
              <Plus size={16} />
              New alert rule
            </button>
            {rules.map(rule => {
              const category = categoryMeta(rule.rule_type)
              const Icon = category.icon
              const condition = ruleCondition(rule)
              return (
                <div key={rule.id} className="p-4 rounded-xl border border-gray-200 bg-white">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg border ${category.color}`}><Icon size={15} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-sm text-gray-900">{rule.name}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${SEVERITY_STYLE[rule.severity || 'WARNING']}`}>
                          {rule.severity || 'WARNING'}
                        </span>
                        {!rule.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Disabled</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-gray-500">
                        <span>{TYPE_LABELS[rule.rule_type] || rule.rule_type}</span>
                        {condition && <span className="font-medium text-gray-700">{condition}</span>}
                        {rule.geofence_name && <span><MapPin size={9} className="inline" /> {rule.geofence_name}</span>}
                        {rule.corridor_name && <span><RouteIcon size={9} className="inline" /> {rule.corridor_name}</span>}
                        <span>{ruleScope(rule)}</span>
                        {rule.customer_name && <span>{rule.customer_name}</span>}
                        {rule.delay_seconds ? <span><Clock size={9} className="inline" /> {Math.round(rule.delay_seconds / 60)} min persistence</span> : null}
                        {(rule.channels || []).map(channel => (
                          <span key={channel}>
                            {channel === 'EMAIL' ? <Mail size={9} className="inline" />
                              : channel === 'WEBHOOK' ? <Webhook size={9} className="inline" />
                                : channel === 'SMS' ? <Radio size={9} className="inline" />
                                  : <Bell size={9} className="inline" />} {channel}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => { if (!rule.is_read_only) { setEditingRule(rule); setPresetVehicleTarget(null); setEditorOpen(true) } }}
                        disabled={rule.is_read_only}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                        title={rule.is_read_only ? 'Global rule (read only)' : 'Edit'}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => { if (!rule.is_read_only) deleteRule(rule) }}
                        disabled={rule.is_read_only}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                        title={rule.is_read_only ? 'Global rule (read only)' : 'Delete'}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
            {rules.length === 0 && (
              <div className="text-center text-xs text-gray-400 py-8">
                Create rules for location, telemetry, sensor or device-event monitoring.
              </div>
            )}
          </div>
        )}
      </div>

      <AlertRuleEditor
        isOpen={editorOpen}
        onClose={() => { setEditorOpen(false); setEditingRule(null); setPresetVehicleTarget(null) }}
        onSaved={loadRules}
        editingRule={editingRule}
        presetVehicleTarget={presetVehicleTarget}
        showConfirm={showConfirm}
        showMessage={showMessage}
      />

      {detailOpen && selectedAlert && (
        <div className="absolute inset-0 z-30 bg-black/20 flex justify-end">
          <div className="w-full max-w-lg h-full bg-white shadow-2xl flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-start justify-between">
              <div>
                <div className="text-xs text-gray-500">{categoryMeta(selectedAlert.alert_type).label}</div>
                <h3 className="font-semibold text-gray-900">{TYPE_LABELS[selectedAlert.alert_type] || selectedAlert.alert_type}</h3>
              </div>
              <button onClick={() => setDetailOpen(false)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {detailLoading ? (
                <div className="h-32 flex items-center justify-center text-sm text-gray-400">
                  <RefreshCw size={15} className="animate-spin mr-2" /> Loading evidence…
                </div>
              ) : (
                <>
                  <div className={`rounded-xl border p-4 ${SEVERITY_STYLE[selectedAlert.severity || 'WARNING']}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle size={16} />
                      <span className="font-semibold text-sm">{selectedAlert.severity || 'WARNING'}</span>
                      <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[selectedAlert.status]}`}>{selectedAlert.status}</span>
                    </div>
                    <p className="text-sm">{selectedAlert.message}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['Vehicle', selectedAlert.vehicle_code || `${selectedAlert.vehicle_source} ${selectedAlert.vehicle_id}`],
                      ['Customer', selectedAlert.customer_name],
                      ['Observed', selectedAlert.observed_value ?? selectedAlert.observed_text],
                      ['Threshold', selectedAlert.threshold_value],
                      ['Signal', selectedAlert.signal_key],
                      ['Source', [selectedAlert.source_brand, selectedAlert.source_event_id ? `event ${selectedAlert.source_event_id}` : null].filter(Boolean).join(' · ')],
                      ['Raised', fmtDate(selectedAlert.created_at)],
                      ['Recovered', selectedAlert.recovered_at ? fmtDate(selectedAlert.recovered_at) : null]
                    ].map(([label, value]) => (
                      <div key={String(label)} className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                        <div className="text-[9px] uppercase tracking-wide text-gray-400">{label}</div>
                        <div className="text-xs font-medium text-gray-800 mt-0.5">{value == null || value === '' ? '—' : String(value)}</div>
                      </div>
                    ))}
                  </div>

                  {(selectedAlert.geofence_name || selectedAlert.corridor_name || selectedAlert.mission_name || selectedAlert.latitude != null) && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">Operational context</h4>
                      <div className="space-y-1.5 text-xs text-gray-600">
                        {selectedAlert.geofence_name && <div><MapPin size={12} className="inline mr-1" />{selectedAlert.geofence_name}</div>}
                        {selectedAlert.corridor_name && <div><RouteIcon size={12} className="inline mr-1" />{selectedAlert.corridor_name}</div>}
                        {selectedAlert.mission_name && <div><Target size={12} className="inline mr-1" />{selectedAlert.mission_name}</div>}
                        {selectedAlert.latitude != null && selectedAlert.longitude != null && (
                          <AlertPlace latitude={Number(selectedAlert.latitude)} longitude={Number(selectedAlert.longitude)} />
                        )}
                      </div>
                    </div>
                  )}

                  {selectedAlert.rule_name && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">Rule</h4>
                      <div className="p-3 rounded-lg border border-gray-200 text-xs">
                        <div className="font-semibold text-gray-900">{selectedAlert.rule_name}</div>
                        {selectedAlert.rule_description && <p className="text-gray-500 mt-1">{selectedAlert.rule_description}</p>}
                        {selectedAlert.fleet_name && <p className="text-gray-500 mt-1">Fleet: {selectedAlert.fleet_name}</p>}
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Notification delivery</h4>
                    {deliveries.length === 0 ? (
                      <p className="text-xs text-gray-400">No channel delivery record.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {deliveries.map(delivery => (
                          <div key={delivery.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 text-xs">
                            {delivery.channel === 'EMAIL' ? <Mail size={12} />
                              : delivery.channel === 'WEBHOOK' ? <Webhook size={12} />
                                : delivery.channel === 'SMS' ? <Radio size={12} />
                                  : <Bell size={12} />}
                            <span className="font-medium">{delivery.channel}</span>
                            <span className="text-gray-400 truncate">{delivery.destination || 'Portal'}</span>
                            <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full ${
                              delivery.delivery_status === 'DELIVERED'
                                ? 'bg-green-100 text-green-700'
                                : delivery.delivery_status === 'FAILED'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-600'
                            }`}>
                              {delivery.delivery_status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedAlert.context_json && Object.keys(selectedAlert.context_json).length > 0 && (
                    <details className="rounded-lg border border-gray-200">
                      <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer">Technical evidence</summary>
                      <pre className="px-3 pb-3 text-[10px] text-gray-500 whitespace-pre-wrap break-words">
                        {JSON.stringify(selectedAlert.context_json, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              {selectedAlert.status === 'ACTIVE' && selectedAlert.can_acknowledge !== false && (
                <button onClick={() => transition(selectedAlert, 'acknowledge')} disabled={busyId === selectedAlert.id}
                  className="px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-medium disabled:opacity-50">
                  Acknowledge
                </button>
              )}
              {!['RESOLVED'].includes(selectedAlert.status) && selectedAlert.can_resolve !== false && (
                <button onClick={() => transition(selectedAlert, 'resolve')} disabled={busyId === selectedAlert.id}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-medium disabled:opacity-50">
                  Resolve
                </button>
              )}
              {selectedAlert.status === 'ACTIVE' && selectedAlert.can_acknowledge === false && selectedAlert.can_resolve === false && (
                <p className="text-[11px] text-gray-400 self-center">
                  You can view this alert but are not allowed to acknowledge or resolve it.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FleetAlertsPanel
